import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { STATUS_LABEL, STATUS_STYLES, APPLIANCE_TYPES, JOB_TYPES, MANUFACTURERS, findLabel, formatDateTime } from "@/lib/options";
import { History, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export const JobHistory = ({ customerId, excludeTicketId, limit, onCount }) => {
  const [tickets, setTickets] = useState(null);

  useEffect(() => {
    if (!customerId) return;
    let alive = true;
    api.get(`/customers/${customerId}/history`)
      .then(({ data }) => {
        if (!alive) return;
        const list = (data.tickets || []).filter((t) => t.id !== excludeTicketId);
        setTickets(list);
        onCount?.(list.length);
      })
      .catch((e) => toast.error(formatApiError(e)));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, excludeTicketId]);

  if (!customerId) return null;
  if (tickets === null) return <div className="text-sm text-slate-400" data-testid="job-history-loading">Loading history…</div>;
  if (tickets.length === 0) return <div className="text-sm text-slate-500" data-testid="job-history-empty">No other visits on record.</div>;

  const shown = limit ? tickets.slice(0, limit) : tickets;

  return (
    <div className="space-y-2" data-testid="job-history-list">
      {shown.map((t) => (
        <Link
          key={t.id}
          to={`/tickets/${t.id}`}
          className="block rounded-md border border-slate-200 bg-slate-50 hover:bg-blue-50 hover:border-blue-200 transition-colors px-3 py-2 group"
          data-testid={`job-history-item-${t.id}`}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-medium text-slate-900 truncate">
              {findLabel(APPLIANCE_TYPES, t.appliance_type)} · {findLabel(JOB_TYPES, t.job_type)}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Badge variant="outline" className={`${STATUS_STYLES[t.status] || ""} rounded-full text-[10px] px-2 py-0`}>
                {STATUS_LABEL[t.status] || t.status}
              </Badge>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-600" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 mt-0.5">
            <div className="text-xs text-slate-500">
              {t.scheduled_at ? formatDateTime(t.scheduled_at) : `Created ${formatDateTime(t.created_at)}`}
              {t.manufacturer ? ` · ${findLabel(MANUFACTURERS, t.manufacturer)}` : ""}
            </div>
            {typeof t.total_charged === "number" && t.total_charged > 0 && (
              <span className="text-xs font-semibold text-emerald-700">${t.total_charged.toFixed(2)}</span>
            )}
          </div>
          {(t.diagnosis || t.job_description) && (
            <div className="text-xs text-slate-600 mt-1 line-clamp-2">
              {t.diagnosis || t.job_description}
            </div>
          )}
        </Link>
      ))}
      {limit && tickets.length > limit && (
        <div className="text-xs text-slate-500 pl-1">…and {tickets.length - limit} more visit(s)</div>
      )}
    </div>
  );
};

export const JobHistoryIcon = History;
